<template>
  <div :class="$style.wrapper">
    <b-row class="px-2 pt-1">
      <b-col
        :cols="12"
        :sm="9"
        class="d-flex justify-content-center justify-content-sm-start pt-2 text-center"
      >
        <BaseBadge variant="light-gray">
          {{ sportName }}
        </BaseBadge>
        <BaseBadge
          variant="light-gray"
          class="mx-1"
        >
          {{ leagueName }}
        </BaseBadge>
        <BaseBadge variant="light-gray">
          {{ roundName }}
        </BaseBadge>
      </b-col>
      <b-col
        :cols="12"
        :sm="3"
        class="d-flex justify-content-center justify-content-sm-end pt-2"
      >
        {{ formattedDate }}
      </b-col>
    </b-row>

    <b-row class="p-2 pb-4">
      <b-col
        :cols="12"
        class="d-flex flex-column align-items-center"
      >
        <span :style="$style.roundName">
          {{ $t('you-won') }}
        </span>
        <BaseTextH1 class="d-flex align-items-center my-1">
          {{ prize }}
          <small>{{ currency }}</small>
        </BaseTextH1>

        <div :class="$style.rankInfoText">
          <template v-if="winCount > 1">
            {{ $t('ending-on-position-x-with-y-players', { position, players: winCount - 1 }) }}
          </template>
          <template v-else>
            {{ $t('ending-on-position-x', { position }) }}
          </template>
        </div>

        <div class="d-flex pt-4">
          <BaseButton
            :variant="isClaimButtonDisabled ? 'orange' : 'outline-orange'"
            font-size="small"
            left-rounded
            :is-disabled="isClaimButtonDisabled"
            @click.native="handleClaimClick"
          >
            <span
              :class="$style.buttonTextWrapper"
              class="pl-2 pr-1"
            >
              <BaseLoading
                v-if="isClaimLoading"
                :min-height="19"
              />
              <template v-else>{{ statusText }}</template>
            </span>
          </BaseButton>

          <router-link
            :to="{ name: 'round-detail', params: { roundId } }"
            class="ml-1"
          >
            <BaseButton
              variant="outline-orange"
              font-size="small"
              right-rounded
            >
              <span
                :class="$style.buttonTextWrapper"
                class="pl-1 pr-2"
              >
                {{ $t('round-detail') }}
              </span>
            </BaseButton>
          </router-link>
        </div>

        <div
          v-if="!isExpired && !isClaimButtonDisabled"
          class="pt-4"
        >
          {{ $t('hurry-up-and-claim-your-prize') }}
          <BaseCountdown
            :date="expirationDate"
          />
          {{ $t('if-you-wait-too-long-you-wont-be-able-to-grap-your') }}
        </div>
      </b-col>
    </b-row>

    <modal
      v-if="isClaimedModalVisible"
      @close="isClaimedModalVisible = false"
    >
      <template slot="body">
        <div class="text-center">
          <BaseTextH1 class="py-2">
            {{ $t('we-will-contact-you-via-email') }}
          </BaseTextH1>
          <div class="d-flex justify-content-center py-4">
            <BaseButton
              variant="outline-orange"
              is-rounded
              @click="isClaimedModalVisible = false"
            >
              <span class="px-2">
                {{ $t('close') }}
              </span>
            </BaseButton>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { format } from 'date-fns'
import BaseButton from '@/components/Atoms/BaseButton'
import BaseTextH1 from '@/components/Atoms/Typography/BaseTextH1'
import BaseBadge from '@/components/Atoms/BaseBadge'
import BaseLoading from '@/components/Atoms/BaseLoading'
import { WIN_STATUSES } from '@/store/modules/prizes/wins'
import Modal from '@/components/Molecules/Modal'
import BaseCountdown from '@/components/Atoms/BaseCountdown'

export default {
  name: 'TheWinOverview',
  components: {
    BaseCountdown,
    Modal,
    BaseLoading,
    BaseBadge,
    BaseButton,
    BaseTextH1,
  },
  props: {
    sportName: {
      type: String,
      required: true,
    },
    leagueName: {
      type: String,
      required: true,
    },
    roundName: {
      type: String,
      required: true,
    },
    roundId: {
      type: Number,
      required: true,
    },
    prize: {
      type: Number,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    position: {
      type: Number,
      required: true,
    },
    date: {
      type: Date,
      required: true,
    },
    type: {
      type: Number,
      required: true,
    },
    winId: {
      type: Number,
      required: true,
    },
    winCount: {
      type: Number,
      required: false,
      default: 0,
    },
    winStatus: {
      type: Number,
      required: true,
    },
    isExpired: {
      type: Boolean,
      required: false,
      default: false,
    },
    expirationDate: {
      type: Date,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isClaimLoading: false,
      formattedDate: format(this.date, 'D.M.YYYY'),
      isClaimedModalVisible: false,
    }
  },
  computed: {
    statusText() {
      switch (this.winStatus) {
      case WIN_STATUSES.READY_TO_CLAIM:
        if (this.isExpired) {
          return this.$t('win-statuses.expired')
        }
        return this.$t('win-statuses.claim-reward')
      case WIN_STATUSES.CLAIMED:
        return this.$t('win-statuses.claimed')
      case WIN_STATUSES.COMPLETE:
        return this.$t('win-statuses.completed')
      }
      return ''
    },
    isClaimButtonDisabled() {
      return this.isExpired ||
        this.isClaimLoading ||
        [WIN_STATUSES.CLAIMED, WIN_STATUSES.COMPLETE].includes(this.winStatus)
    },
  },
  methods: {
    ...mapActions({
      claimReward: 'prizes/wins/claimReward',
    }),
    handleClaimClick() {
      if (this.isClaimButtonDisabled) {
        return true
      }
      this.isClaimLoading = true
      this.claimReward({ winId: this.winId }).then(() => {
        this.isClaimLoading = false
        this.isClaimedModalVisible = true
      })
    },
  },
}
</script>

<style module lang="scss">
  @import "../../../assets/scss/variables";

  .wrapper {
    width: 100%;
    border-bottom: 1px solid $color-bleached-silk;
    transition: all .3s ease-in-out;
    background-color: $color-white;

    &:hover {
      background-color: $color-bleached-silk;
    }
    .rankInfoText {
      font-size: $font-size-small;
    }
    .buttonTextWrapper {
      width: 105px;
    }
  }
</style>
